import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { CheckCircleFilled } from "@ant-design/icons";
import { Empty } from "antd";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../store";
import { getNotificationsThunk } from "../../../store/actions/social.action";
import { timeAgo } from "../../../core/utils/timeAgo";
import Avatar from "boring-avatars";
import { listCommunities } from "../../../constants/communities";

const getCountryFlag = (countryCode: string) => {
  const community = listCommunities.find((c) => c.countryCode === countryCode);
  if (!community) return "";

  if (countryCode === "KARDO") {
    return (
      <img
        src={community.flag}
        alt="Kardo"
        style={{ width: "40px", height: "40px", objectFit: "contain" }}
      />
    );
  }

  return community.flag;
};

export const RightSidebar = () => {
  const navigate = useNavigate();
  const { notifications } = useAppSelector((state) => state.socialReducer);
  const [notificationsPage, setNotificationsPage] = useState(1);
  const [isLoadingMoreNotifications, setIsLoadingMoreNotifications] =
    useState(false);
  const [hasMoreNotifications, setHasMoreNotifications] = useState(true);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();

  const fetchNotifications = async (
    page: number = 1,
    append: boolean = false
  ) => {
    try {
      setIsLoadingMoreNotifications(true);
      const result = await dispatch(
        getNotificationsThunk({ page, limit: 10 })
      ).unwrap();

      if (result) {
        if (append) {
          // Append new notifications to existing ones
          dispatch({
            type: "social/getNotifications/fulfilled",
            payload: {
              ...result,
              data: [...notifications.data, ...result.data],
            },
          });
        }

        // Check if there are more notifications to load
        setHasMoreNotifications(result.data.length === 10);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setIsLoadingMoreNotifications(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  useEffect(() => {
    const handleNotificationsScroll = () => {
      if (!notificationsRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } =
        notificationsRef.current;

      // Load more when user has scrolled to 80% of the content
      if (
        scrollHeight - scrollTop <= clientHeight * 1.2 &&
        !isLoadingMoreNotifications &&
        hasMoreNotifications
      ) {
        const nextPage = notificationsPage + 1;
        setNotificationsPage(nextPage);
        fetchNotifications(nextPage, true);
      }
    };

    const notificationsContainer = notificationsRef.current;
    if (notificationsContainer) {
      notificationsContainer.addEventListener(
        "scroll",
        handleNotificationsScroll
      );
    }

    return () => {
      if (notificationsContainer) {
        notificationsContainer.removeEventListener(
          "scroll",
          handleNotificationsScroll
        );
      }
    };
  }, [
    notificationsPage,
    isLoadingMoreNotifications,
    hasMoreNotifications,
    notifications.data,
  ]);

  return (
    <RightSidebarWrapper>
      <SidebarSection>
        <SectionTitle>Recent Activities</SectionTitle>
        <SubscribersList ref={notificationsRef}>
          {notifications.data.map((notification, index) => (
            <SubscriberItem
              key={index}
              onClick={() => {
                if (notification?.post?._id) {
                  navigate(`/post/${notification?.post?._id}`);
                }
              }}
            >
              <PurchaseTopRow>
                {notification.sender.avatarUrl ? (
                  <PurchaseAvatar src={notification.sender.avatarUrl} />
                ) : (
                  <Avatar
                    name={
                      notification.sender.firstName +
                      " " +
                      notification.sender.lastName
                    }
                    variant="beam"
                    size={48}
                  />
                )}
                <PurchaseUserInfo>
                  <PurchaseUserName>
                    {notification.sender.firstName}{" "}
                    {notification.sender.lastName}
                    {notification.sender.paymentMode === 4 && <Verified />}
                  </PurchaseUserName>
                  <PurchaseActionRow>
                    <PurchaseActionText>
                      {notification.type === "comment" &&
                        "commented on your post"}
                      {notification.type === "react" &&
                        (notification.comment
                          ? "reacted to your comment"
                          : "reacted to your post")}
                      {notification.type === "reply" &&
                        "replied to your comment"}
                      {notification.type === "post" && "posted a post"}
                      <PurchaseTimeText>
                        {timeAgo(notification.createdAt)}
                      </PurchaseTimeText>
                    </PurchaseActionText>
                  </PurchaseActionRow>
                </PurchaseUserInfo>
                {notification.type === "post" &&
                  notification?.post?.countryCode && (
                    <CountryFlag>
                      {getCountryFlag(notification?.post?.countryCode)}
                    </CountryFlag>
                  )}
              </PurchaseTopRow>
            </SubscriberItem>
          ))}
          {notifications.data.length === 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                height: "100%",
                width: "100%",
              }}
            >
              <StyledEmpty
                description={
                  <span style={{ color: "#686868" }}>No notifications</span>
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </div>
          )}
        </SubscribersList>
      </SidebarSection>
    </RightSidebarWrapper>
  );
};

const RightSidebarWrapper = styled.div`
  width: 25%;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  @media (max-width: 1400px) {
    display: none;
  }
`;

const SidebarSection = styled.div`
  background: #282828;
  border-radius: 24px;
  padding: 1.2rem;
`;

const SectionTitle = styled.h3`
  color: #fff;
  font-size: 1.2rem;
  font-weight: 500;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;

  .anticon {
    color: #60a5fa;
  }
`;

const HotelBookingCard = styled.div`
  background: #222222;
  border-radius: 16px;
  overflow: hidden;
`;

const HotelImage = styled.img`
  width: 100%;
  height: 160px;
  object-fit: cover;
`;

const HotelInfo = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const HotelName = styled.h4`
  color: #fff;
  font-size: 1.1rem;
  font-weight: 600;
  align-self: flex-start;
  margin: 0;
`;

const HotelDescription = styled.p`
  color: #bdbdbd;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  line-height: 1.4;
  align-self: flex-start;
`;

const BookButton = styled.button`
  background: linear-gradient(45deg, #ff6b6b, #ff8e53);
  color: #fff;
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  position: relative;
  overflow: hidden;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      120deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    transition: 0.5s;
  }

  &:hover {
    box-shadow: 0 6px 20px rgba(255, 107, 107, 0.4),
      0 0 20px rgba(255, 142, 83, 0.3);
    background: linear-gradient(45deg, #ff8e53, #ff6b6b);

    &:before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px);
    box-shadow: 0 2px 10px rgba(255, 107, 107, 0.3);
  }
`;

const SubscribersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: 600px;
  overflow-y: auto;
  padding-right: 6px;

  /* Hide default scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #1a1924;
    border: 1px solid #242424;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #333333;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #363636;
  }
`;

const SubscriberItem = styled.div`
  background: #232323;
  border-radius: 18px;
  padding: 1.2rem;
  color: #fff;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  transition: ease 0.3s;
  cursor: pointer;
  position: relative;

  &:hover {
    background-color: #1f1f1f;
  }
`;

const CountryFlag = styled.div`
  font-size: 1.2rem;
`;

const PurchaseTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  justify-content: space-between;
`;

const PurchaseAvatar = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
`;

const PurchaseUserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  flex: 1;
`;

const PurchaseUserName = styled.div`
  font-weight: 600;
  font-size: 1.1rem;
`;

const PurchaseActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PurchaseActionText = styled.span`
  color: #bdbdbd;
  font-size: 0.95rem;
`;

const PurchaseDot = styled.span`
  width: 7px;
  height: 7px;
  background: #ff8e53;
  border-radius: 50%;
  display: inline-block;
  margin: 0;
  padding: 0;
`;

const PurchaseTimeText = styled.span`
  color: #ff8e53;
  font-size: 0.95rem;
  margin-left: 0.3rem;
`;

const PurchaseBottomRow = styled.div`
  margin-top: 1.2rem;
  background: #1a1a1a;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.7rem 1rem;
`;

const PurchasePrice = styled.span`
  font-size: 1.2rem;
  font-weight: 600;
  color: #ff8e53;
`;

const PurchaseThankedButton = styled.button`
  background: #393939;
  color: #ff8e53;
  border: none;
  border-radius: 8px;
  padding: 0.6rem 1rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  align-self: flex-end;
`;

const StyledEmpty = styled(Empty)`
  .ant-empty-image {
    svg {
      fill: #686868;
    }
  }
`;

const Verified = styled(CheckCircleFilled)`
  color: #ff8e53;
  font-size: 1rem;
  margin-left: 0.4rem;
`;
